import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import * as React from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { captureException } from "~/config/sentry";
import { useFinalizeUploadedTaskAttachmentMutation } from "~/config/uppy/UppyConfig.graphql";
import { usePrepareTaskAttachmentForUploadMutation } from "~/screens/App/screens/Project/Tasks/components/TaskDialog/components/TaskEdit/components/UpdateTaskForm/components/AddAttachmentButton/AddAttachmentButton.graphql";
import { blobToBase64 } from "./blobUtils";
import { ensureDirectoryExists } from "./fsUtils";
import { saveToIndexedDB } from "./indexedDBStorage";
import {
  type OfflineTaskUpload,
  useOfflineTaskUploadsStore,
} from "./offlineTaskUploads.store";
import { useIsOffline } from "./useIsOffline.hook";

type UploadQueueItem = {
  uploadId: string;
  taskId: string;
  file: File;
};

type AddOfflineUploadOptions = {
  deferUntilTaskExists?: boolean;
  projectId?: string;
  visitId?: string;
};

const isNative = Capacitor.isNativePlatform();

const MAX_FILE_SIZE = 500 * 1024 * 1024;
const MAX_TOTAL_STORAGE = 2 * 1024 * 1024 * 1024;

export const useOfflineTaskUploads = () => {
  const { t } = useTranslation();
  const isOffline = useIsOffline();
  const [uploadQueue, setUploadQueue] = React.useState<UploadQueueItem[]>([]);
  const [currentUpload, setCurrentUpload] =
    React.useState<UploadQueueItem | null>(null);
  const processingRef = React.useRef(false);

  const {
    addUpload,
    ensureUploadKey,
    getStoredFile,
    getUploadsForTask,
    isProcessing,
    markUploadAsProcessed,
    processPendingUploads,
    removeUpload,
    resetIncompleteUploads,
    retryFailedUploads,
    updateTaskId,
    updateUploadStatus,
    uploads,
  } = useOfflineTaskUploadsStore();

  const [, prepareTaskAttachmentForUpload] =
    usePrepareTaskAttachmentForUploadMutation();
  const [, finalizeUploadedTaskAttachment] =
    useFinalizeUploadedTaskAttachmentMutation();

  const processUploadFromStore = React.useCallback(
    async (upload: OfflineTaskUpload) => {
      let stage = "unknown";
      try {
        const currentUploadState = useOfflineTaskUploadsStore
          .getState()
          .uploads.find((u) => u.id === upload.id);
        if (!currentUploadState || currentUploadState.status !== "uploading") {
          return;
        }

        const file = await getStoredFile(upload.id);
        if (!file) {
          stage = "getStoredFile";
          captureException(new Error("Stored file not found"), {
            extra: {
              stage,
              upload,
            },
            tags: {
              component: "offline-storage",
              function: "processUploadFromStore",
            },
          });
          throw new Error("Stored file not found");
        }
        const uploadKey = ensureUploadKey(upload.id);
        if (!uploadKey) {
          throw new Error("Upload state not found");
        }
        const { data: prepareData, error: prepareError } =
          await prepareTaskAttachmentForUpload({
            input: {
              key: uploadKey,
              name: upload.fileName,
              taskId: upload.taskId,
            },
          });

        if (
          prepareError ||
          prepareData?.prepareTaskAttachmentForUpload?.__typename ===
            "PrepareTaskAttachmentForUploadErrors"
        ) {
          stage = "prepareUpload";
          captureException(new Error("Failed to prepare upload"), {
            extra: {
              prepareData:
                prepareData?.prepareTaskAttachmentForUpload?.__typename,
              prepareError,
              stage,
              upload,
            },
            tags: {
              component: "offline-storage",
              function: "processUploadFromStore",
            },
          });
          throw new Error("Failed to prepare upload");
        }

        const { key, url } = prepareData?.prepareTaskAttachmentForUpload ?? {};

        if (!key || !url) {
          stage = "prepareUpload";
          captureException(new Error("Invalid upload response"), {
            extra: {
              key,
              stage,
              upload,
              url,
            },
            tags: {
              component: "offline-storage",
              function: "processUploadFromStore",
            },
          });
          throw new Error("Invalid upload response");
        }

        const uploadWithProgress = (): Promise<void> => {
          return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            xhr.upload.addEventListener("progress", (event) => {
              if (event.lengthComputable) {
                const progress = Math.round((event.loaded / event.total) * 100);
                updateUploadStatus(upload.id, "uploading", progress);
              }
            });

            xhr.addEventListener("load", () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
              } else {
                stage = "uploadWithProgress";
                reject(new Error(`Upload failed with status ${xhr.status}`));
              }
            });

            xhr.addEventListener("error", () => {
              stage = "uploadWithProgress";
              reject(new Error("Upload failed"));
            });

            xhr.open("PUT", url);
            xhr.setRequestHeader("Content-Type", upload.fileType);
            xhr.send(file);
          });
        };

        await uploadWithProgress();

        const { data: finalizeData, error: finalizeError } =
          await finalizeUploadedTaskAttachment(
            {
              input: { key },
            },
            {
              additionalTypenames: [
                "tasks",
                "task_attachments",
                "projects_by_pk",
                "projects",
                "project_labels",
                "tasks_aggregate",
              ],
            }
          );

        if (
          finalizeError ||
          finalizeData?.finalizeUploadedTaskAttachment?.__typename ===
            "FinalizeUploadedTaskAttachmentErrors"
        ) {
          stage = "finalizeUpload";
          captureException(new Error("Failed to finalize upload"), {
            extra: {
              finalizeData:
                finalizeData?.finalizeUploadedTaskAttachment?.__typename,
              finalizeError,
              stage,
              upload,
            },
            tags: {
              component: "offline-storage",
              function: "processUploadFromStore",
            },
          });
          throw new Error("Failed to finalize upload");
        }
        updateUploadStatus(upload.id, "complete", 100);

        markUploadAsProcessed(upload.id);

        setTimeout(() => {
          removeUpload(upload.id);
        }, 2000);

        setTimeout(() => {
          const { processPendingUploads } =
            useOfflineTaskUploadsStore.getState();
          void processPendingUploads();
        }, 1000);
      } catch (error) {
        captureException(error, {
          extra: {
            stage,
            upload,
          },
          tags: {
            component: "offline-storage",
            function: "processUploadFromStore",
          },
        });
        const errorMessage =
          error instanceof Error
            ? error.message
            : t(
                "screens.ProjectTasksListScreen.UpdateTaskForm.unknownUploadError",
                "Unknown error during upload"
              );
        updateUploadStatus(upload.id, "error", 0, errorMessage);
      }
    },
    [
      ensureUploadKey,
      getStoredFile,
      prepareTaskAttachmentForUpload,
      finalizeUploadedTaskAttachment,
      updateUploadStatus,
      markUploadAsProcessed,
      removeUpload,
    ]
  );

  React.useEffect(() => {
    void resetIncompleteUploads();
  }, [resetIncompleteUploads]);

  React.useEffect(() => {
    if (!isOffline) {
      const nonTemporaryPendingUploads = uploads.some(
        (u) =>
          u.status === "pending" && !u.taskId.startsWith("temp_") && !u.visitId
      );
      const nonTemporaryErrorUploads = uploads.some(
        (u) =>
          u.status === "error" && !u.taskId.startsWith("temp_") && !u.visitId
      );

      if (nonTemporaryErrorUploads) {
        void retryFailedUploads();
      }

      if (nonTemporaryPendingUploads || nonTemporaryErrorUploads) {
        void processPendingUploads();
      }
    }
  }, [isOffline, uploads, processPendingUploads, retryFailedUploads]);

  React.useEffect(() => {
    const pendingUploads = uploads.filter((u) => u.status === "pending");
    const nonTemporaryPendingUploads = pendingUploads.filter(
      (u) => !u.taskId.startsWith("temp_") && !u.visitId
    );

    if (nonTemporaryPendingUploads.length > 0 && !isOffline && !isProcessing) {
      void processPendingUploads();
    }
  }, [uploads, isOffline, isProcessing, processPendingUploads]);

  React.useEffect(() => {
    const processNextUpload = () => {
      if (uploadQueue.length > 0 && !currentUpload && !isProcessing) {
        const nextUpload = uploadQueue[0];
        setCurrentUpload(nextUpload);
        setUploadQueue((prevQueue) => {
          return prevQueue.slice(1);
        });
      }
    };

    processNextUpload();
  }, [uploadQueue, currentUpload, isProcessing]);

  React.useEffect(() => {
    const uploadingUploads = uploads.filter((u) => u.status === "uploading");

    const nonTemporaryUploadingUploads = uploadingUploads.filter(
      (u) => !u.taskId.startsWith("temp_") && !u.visitId
    );

    if (
      nonTemporaryUploadingUploads.length > 0 &&
      !isOffline &&
      !currentUpload &&
      !processingRef.current
    ) {
      const uploadToProcess = nonTemporaryUploadingUploads[0];

      processingRef.current = true;
      const { setProcessing } = useOfflineTaskUploadsStore.getState();
      setProcessing(true);

      void processUploadFromStore(uploadToProcess).finally(() => {
        setProcessing(false);
        processingRef.current = false;
      });
    }
  }, [uploads, isOffline, currentUpload, processUploadFromStore]);

  const addOfflineUpload = React.useCallback(
    async (
      taskId: string,
      file: File,
      options: AddOfflineUploadOptions = {}
    ) => {
      try {
        if (file.size > MAX_FILE_SIZE) {
          const errorMessage = `File size (${(
            file.size /
            (1024 * 1024)
          ).toFixed(1)} MB) exceeds the maximum allowed size of ${
            MAX_FILE_SIZE / (1024 * 1024)
          } MB`;
          captureException(new Error(errorMessage), {
            extra: {
              currentFileSize: file.size,
              file,
              maxFileSize: MAX_FILE_SIZE,
              taskId,
            },
            tags: {
              component: "offline-storage",
              function: "addOfflineUpload",
            },
          });
          throw new Error(errorMessage);
        }

        const currentUploads = useOfflineTaskUploadsStore.getState().uploads;
        const currentTotalSize = currentUploads.reduce(
          (total, upload) => total + upload.fileSize,
          0
        );
        const newTotalSize = currentTotalSize + file.size;

        if (newTotalSize > MAX_TOTAL_STORAGE) {
          const errorMessage = `Total storage would exceed the maximum allowed size of ${
            MAX_TOTAL_STORAGE / (1024 * 1024 * 1024)
          } GB`;
          captureException(new Error(errorMessage), {
            extra: {
              currentTotalSize,
              file,
              maxTotalStorage: MAX_TOTAL_STORAGE,
              newTotalSize,
              taskId,
            },
            tags: {
              component: "offline-storage",
              function: "addOfflineUpload",
            },
          });
          throw new Error(errorMessage);
        }

        const uploadId = `${taskId}-${Date.now()}-${Math.random()}`;

        const normalizedFileName = file.name
          .replace(/[^a-zA-Z0-9.-]/g, "_")
          .replace(/_{2,}/g, "_");

        const fileExtension = normalizedFileName.split(".").pop() ?? "";
        const safeFileName = `${uploadId}.${fileExtension}`;
        const path = `offline-task-uploads/${taskId}/${safeFileName}`;

        if (isNative) {
          await ensureDirectoryExists(path);

          const base64Data = await blobToBase64(file);

          await Filesystem.writeFile({
            data: base64Data,
            directory: Directory.Data,
            path,
          });
        } else {
          await saveToIndexedDB(path, file);
        }

        await addUpload({
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          id: uploadId,
          projectId: options.projectId,
          retryCount: 0,
          taskId,
          visitId: options.visitId,
        });

        if (!isOffline && !options.deferUntilTaskExists && !options.visitId) {
          setUploadQueue((prevQueue) => [
            ...prevQueue,
            { file, taskId, uploadId },
          ]);
        }

        toast.success(
          t(
            "screens.ProjectTasksListScreen.UpdateTaskForm.offlineFileAdded",
            "{{count}} file(s) added for offline upload",
            { count: 1 }
          )
        );
      } catch (error) {
        console.error("Error in addOfflineUpload:", error);

        const errorMessage =
          error instanceof Error
            ? error.message
            : t(
                "screens.ProjectTasksListScreen.UpdateTaskForm.unknownError",
                "Unknown error"
              );
        toast.error(
          t(
            "screens.ProjectTasksListScreen.UpdateTaskForm.offlineUploadError",
            "Error adding file for offline upload: {{error}}",
            { error: errorMessage }
          )
        );

        throw error;
      }
    },
    [addUpload, isOffline, t]
  );

  React.useEffect(() => {
    const pendingUploads = uploads.filter((u) => u.status === "pending");

    const nonTemporaryUploads = pendingUploads.filter(
      (u) => !u.taskId.startsWith("temp_") && !u.visitId
    );

    if (nonTemporaryUploads.length > 0 && !isOffline && !isProcessing) {
      void processPendingUploads();
    }
  }, [uploads, isOffline, isProcessing, processPendingUploads]);

  React.useEffect(() => {
    const errorUploads = uploads.filter((u) => u.status === "error");

    const nonTemporaryErrorUploads = errorUploads.filter(
      (u) => !u.taskId.startsWith("temp_") && !u.visitId
    );

    if (nonTemporaryErrorUploads.length > 0 && !isOffline) {
      nonTemporaryErrorUploads.forEach((upload) => {
        const maxRetries = 3;

        if (upload.retryCount < maxRetries) {
          const delay = Math.pow(2, upload.retryCount) * 1000;

          setTimeout(() => {
            const { retryFailedUploads } =
              useOfflineTaskUploadsStore.getState();
            retryFailedUploads();
          }, delay);
        }
      });
    }
  }, [uploads.filter((u) => u.status === "error").length, isOffline]);

  return {
    addOfflineUpload,

    getStoredFile,

    getUploadsForTask,

    isProcessing,

    removeUpload,

    updateTaskId,
    updateUploadStatus,
    uploads,
  };
};
